export const project = 'taggingng'; // need this for when we dynamically import utils

export const sending = (configuration) => {
	const reportsForEventViewer = [];
	const reportsForNewRelic = [];

	const { data = {}, event, groupingSending, win } = configuration;

	const { additionalData, digitalData } = data;

	let { directCallEvent } = data;
	let groupingPayload;

	if (groupingSending) {
		({ directCallEvent, payload: groupingPayload } =
			groupingSending(configuration));
		if (groupingPayload) {
			reportsForEventViewer.push({
				event,
				payload: groupingPayload,
				sender: 'DDC.pushToGtmDataLayer'
			});
			// since grouping sending is an optional addition we don't need extra NewRelic reporting here
			// lower down NewRelic reporting is taken care of
		}
	}

	window.digitalData = {
		...(win.digitalData || {}),
		...digitalData
	};

	if (win._satellite !== undefined && event !== 'ddc.wstagging.first') {
		const trackArgs = [directCallEvent];
		if (additionalData) {
			trackArgs.push(additionalData);
		}
		win._satellite.track(...trackArgs);

		if (digitalData) {
			reportsForEventViewer.push({
				event,
				payload: digitalData,
				sender: 'digitalData'
			});
		}

		reportsForEventViewer.push({
			event,
			payload: {
				...(additionalData && { additionalData }),
				directCallEvent
			},
			sender: '_satellite.track'
		});
		reportsForNewRelic.push({
			clientEventName: directCallEvent,
			sender: '_satellite.track'
		});
	} else {
		reportsForEventViewer.push({
			payload: digitalData,
			sender: 'digitalData'
		});
		reportsForNewRelic.push({
			clientEventName: 'page load digitalData',
			sender: 'digitalData'
		});
	}

	return {
		reportsForEventViewer,
		reportsForNewRelic
	};
};

export const getFranchises = () =>
	window?.DDC?.dataLayer?.site?.franchises?.join('|');

export const getOrientation = () => {
	const orientation =
		window.screen.msOrientation ||
		window.screen.mozOrientation ||
		(window.screen.orientation || {}).type;
	if (!orientation) {
		return window.screen.width > window.screen.height
			? 'landscape'
			: 'portrait';
	} else {
		return {
			'landscape-primary': 'landscape',
			'landscape-secondary': 'landscape',
			'portrait-primary': 'portrait',
			'portrait-secondary': 'portrait'
		}[orientation];
	}
};
export const getViewport = () => `${window.innerWidth}x${window.innerHeight}`;

export const getInternalSearchTerms = () =>
	new URLSearchParams(window.location.search).get('search');

export const getVehiclesToCompare = ({ eventData }) => {
	const inventoryType = {
		1: 'used',
		2: 'new',
		3: 'cpo',
		new: 'new',
		used: 'used',
		certified: 'cpo',
		all: 'all',
		default: 'all'
	};
	return eventData.vehicles.map((vehicle) => {
		return {
			modelYear: vehicle.year,
			make: vehicle.make,
			modelName: vehicle.model,
			trimLevel: vehicle.trim,
			vin: vehicle.vin,
			condition: inventoryType[vehicle.condition]
		};
	});
};

const isOffersPage = (alias) => {
	return (
		(!alias.includes('PARTS') && alias.includes('SPECIALS')) ||
		alias.includes('COUPON') ||
		alias.includes('INCENTIVES')
	);
};

const isFinancePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('FINANCE')) ||
		alias.includes('FINANCING')
	);
};

const isTradeInPage = (alias) => {
	return alias.includes('TRADE') && !alias.includes('CONFIRM');
};

const isToolPage = (alias) => {
	return (
		alias.includes('INVENTORY_COMPARE') ||
		alias.includes('CALCULATOR_PAYMENT') ||
		isTradeInPage(alias) ||
		alias.includes('INVENTORY_FINDER')
	);
};

const isServicePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('SERVICE')) ||
		alias.includes('PARTS') ||
		alias.includes('MOPAR') ||
		alias.includes('XTIME')
	);
};

const isHoursOrDirectionsPage = (alias) => {
	return alias.includes('HOURS') || alias.includes('DIRECTIONS');
};

const isCorporatePage = (alias) => {
	return (
		alias.includes('PRIVACY') ||
		alias.includes('LEGAL') ||
		alias.includes('SITEMAP')
	);
};

const isAboutOrStaffPage = (alias) => {
	return (
		alias.includes('ABOUT') ||
		alias.includes('STAFF_LISTING') ||
		alias.includes('EMPLOYMENT_LISTING')
	);
};

export const getSiteSection = (level) => {
	const {
		DDC: {
			dataLayer: {
				page: {
					attributes: { incentives = [] } = {},
					pageInfo: {
						isVdp = false,
						isVlp = false,
						pageName = ''
					} = {}
				} = {}
			} = {}
		} = {}
	} = window;

	let siteSectionsLevel1;
	let siteSectionsLevel2;
	let siteSectionsLevel3;
	let siteSectionsLevel4;

	const { modelYear, model } = window?.DDC?.dataLayer?.vehicles?.[0] || {};

	/* only site sections levels 1-3 method of evaluation differ per page alias, 
		4 & 5 are calculated using the same methods for each page alias */
	if (isVdp) {
		siteSectionsLevel1 = 'vehicles';
		siteSectionsLevel2 = `${modelYear} ${model}`;
	} else if (isVlp) {
		siteSectionsLevel1 = 'vehicles';
	} else if (isFinancePage(pageName)) {
		siteSectionsLevel1 = 'financing';
	} else if (isOffersPage(pageName) || pageName.includes('PROMOTIONS')) {
		siteSectionsLevel1 = 'offers';
		if (pageName.includes('INCENTIVES') && incentives.length) {
			const { applicableCondition, currentVehicleModel, applicableYear } =
				incentives[0];
			siteSectionsLevel2 = applicableCondition;
			siteSectionsLevel3 =
				currentVehicleModel &&
				applicableYear &&
				`${applicableYear} ${currentVehicleModel}`;
		}
	} else if (isToolPage(pageName)) {
		siteSectionsLevel1 = 'tool';
	} else if (isServicePage(pageName)) {
		siteSectionsLevel1 = 'services';
	} else if (
		isHoursOrDirectionsPage(pageName) ||
		isAboutOrStaffPage(pageName) ||
		pageName.includes('CONTACT')
	) {
		siteSectionsLevel1 = 'dealer-information';
	} else if (isCorporatePage(pageName)) {
		siteSectionsLevel1 = 'corporate';
	} else if (pageName.includes('SHOWROOM')) {
		siteSectionsLevel1 = 'vehicles';
		siteSectionsLevel2 = modelYear && `${modelYear} ${model}`;
		if (siteSectionsLevel2) {
			siteSectionsLevel3 = 'Model Showroom';
		} else {
			siteSectionsLevel2 = 'Showroom';
		}
	}

	// Adding in the page alias to make the pageName more unique.
	// Pages differ in number of descriptors.
	// The page alias for Showroom pages is already included in siteSectionsLevel2 or siteSectionsLevel3.
	if (!pageName.includes('SHOWROOM')) {
		// Format the page alias to be used in the pageName:
		// Remove the word "sitebuilder", remove trailing numbers, change underscores to spaces, and make lowercase.
		const formattedAlias =
			(pageName &&
				pageName
					.replace(/(?:sitebuilder|(?:_\d+)+$)/gi, '')
					.replace(/_/g, ' ')
					.toLowerCase()
					.trim()) ||
			'';

		if (siteSectionsLevel1 && siteSectionsLevel2 && siteSectionsLevel3) {
			siteSectionsLevel4 = formattedAlias;
		} else if (siteSectionsLevel1 && siteSectionsLevel2) {
			siteSectionsLevel3 = formattedAlias;
		} else if (siteSectionsLevel1) {
			siteSectionsLevel2 = formattedAlias;
		} else {
			siteSectionsLevel1 = formattedAlias;
		}
	}

	// join all the sections together by : if they are defined and not empty string
	const siteSectionsLevel5 = [
		siteSectionsLevel1,
		siteSectionsLevel2,
		siteSectionsLevel3,
		siteSectionsLevel4
	]
		.filter((section) => !!section)
		.join(':');

	return [
		siteSectionsLevel1,
		siteSectionsLevel2,
		siteSectionsLevel3,
		siteSectionsLevel4,
		siteSectionsLevel5
	][level - 1];
};

export const getSiteSection1 = () => getSiteSection(1);
export const getSiteSection2 = () => getSiteSection(2);
export const getSiteSection3 = () => getSiteSection(3);
export const getSiteSection4 = () => getSiteSection(4);
export const getSiteSection5 = () => getSiteSection(5);

export const getAppropriateFormName = ({
	eventData: { trackingSection, trackingId }
}) => {
	return `[[mapping.form.${trackingId}.formName.${
		trackingSection === 'Global Nav' ? 'nav' : 'default'
	}]]`;
};

export const getDefaultFormOnPagePayload = ({
	eventData,
	eventData: { vehicle = {}, formEventId }
}) => ({
	...vehicle,
	formName: getAppropriateFormName({ eventData }),
	formEventId,
	formCategory: '[[mapping.form.[[eventData.trackingId]].formCategory]]',
	formType: '[[mapping.form.[[eventData.trackingId]].formType]]'
});

export const getFormFieldName = ({ eventData }) => {
	if (['input', 'select', 'textarea'].includes(eventData.activeElementTag)) {
		return eventData.activeElement;
	}

	return undefined;
};

export const getFormInitiationDigitalData = ({ eventData }) => {
	if (['input', 'select', 'textarea'].includes(eventData.activeElementTag)) {
		return {
			formInfo: {
				formFieldName: eventData.activeElement,
				formName: eventData.trackingSection
			}
		};
	}

	return undefined;
};

export const getFilteredSearchFacetsStr = ({ eventData }) => {
	const { queryFacets } = eventData;
	if (!queryFacets) return '';
	const filters = [];
	Object.keys(queryFacets).forEach((fType) => {
		if (fType !== 'compositeType' && fType !== 'search') {
			let fSelected;
			if (typeof queryFacets[fType] === 'string') {
				fSelected = queryFacets[fType].split(',');
				fSelected.forEach((fValue) => {
					filters.push(`${fType}-${fValue}`);
				});
			} else {
				filters.push(`${fType}-${queryFacets[fType]}`);
			}
		}
	});
	return filters.join('|');
};

export const getTelephone = ({ eventData } = {}) =>
	eventData?.url?.replace?.('tel:', '');

export const getAdditionalDataForNextIfHasSlideData = ({
	eventData: { slidePositionPostClick, slideTotal, attributes }
}) => {
	const additionalData = {};
	const nextArrow =
		attributes.class?.includes('next') || attributes.rel?.includes('next');

	if (slidePositionPostClick && slideTotal) {
		if (nextArrow) {
			additionalData.position =
				slidePositionPostClick === 1
					? slideTotal
					: slidePositionPostClick - 1;
		} else {
			additionalData.position =
				slidePositionPostClick === slideTotal
					? slideTotal
					: slidePositionPostClick + 1;
		}

		additionalData.interactionValue = `${additionalData.position}/${slideTotal}`;
	}

	return additionalData;
};

export const getElementForLinkClicks = ({ eventData: { attributes } }) => {
	const {
		id,
		class: className,
		rawHref: href,
		tagName,
		innerText,
		type
	} = attributes;
	const element = {};

	if (id) {
		element.id = id;
	}

	if (className) {
		element.className = className;
	}

	if (href) {
		element.href = href;
	}

	if (tagName) {
		element.tagName = tagName;
	}

	if (innerText) {
		element.innerText = innerText;
	}

	if (type) {
		element.type = type;
	}

	return JSON.stringify(element);
};

export const getLinkType = ({
	eventData: { attributes = {}, containsImage }
}) => {
	const { tagName = '', class: className = '', rel = '' } = attributes;
	// NOTE: containsImage is a ws-tracking property that effectively deals with 'a img' selectors
	// Also a lot of the image.link directCallEvents are from anchor tags that contain images.

	const ARROW_PREV =
		(tagName === 'BUTTON' && className.includes('prev')) ||
		(className.includes('slick-prev') &&
			className.includes('arrow-left')) ||
		(tagName === 'A' && rel.includes('prev')) ||
		(tagName === 'A' && className.includes('prev'));

	const ARROW_NEXT =
		(tagName === 'BUTTON' && className.includes('next')) ||
		(className.includes('slick-next') &&
			className.includes('arrow-right')) ||
		(tagName === 'A' && rel.includes('next')) ||
		(tagName === 'A' && className.includes('next'));

	const IMAGE_LINK = tagName === 'IMG' || containsImage;
	const SEARCH_LINK =
		className.includes('ddc-btn-internetprice') ||
		className.includes('submit-search');

	const TEXT_LINK = tagName === 'A';
	const BUTTON_LINK = tagName === 'BUTTON' || className.includes('btn');

	if (ARROW_PREV) {
		return 'arrow.prev';
	}

	if (ARROW_NEXT) {
		return 'arrow.next';
	}

	if (IMAGE_LINK) {
		return 'image.link';
	}

	if (SEARCH_LINK) {
		return 'search.link';
	}

	if (TEXT_LINK) {
		return 'text.link';
	}

	if (BUTTON_LINK) {
		return 'button.link';
	}

	return undefined;
};

export const getMediaEventDirectCallEvent = ({ eventData }) => {
	const { action, element } = eventData;

	if (action === 'swiped') {
		return 'carousel swiped';
	} else {
		const arrowButtonMatch = element.match(/(?:next|previous)/i);
		if (arrowButtonMatch) {
			return `arrow.${arrowButtonMatch[0].substring(0, 4).toLowerCase()}`;
		} else if (/button/i.test(element)) {
			return 'button.link';
		}
	}
	return 'image.link';
};

export const getSpecialsEventDirectCallEvent = ({ eventData }) => {
	const { specialCategory } = eventData;
	const specialCategoryLowerCase = specialCategory.toLowerCase();
	if (
		specialCategoryLowerCase === 'service' ||
		specialCategoryLowerCase === 'parts'
	) {
		return `${specialCategoryLowerCase}.offer.click`;
	}
	return 'offer.click';
};

export const getSpecialsEventCustomLink = ({ eventData }) => {
	const {
		element,
		specialCategory,
		specialPlacementType,
		section,
		specialCta,
		specialCtaType,
		specialHeading,
		specialYear,
		specialMake,
		specialModel,
		specialIncentiveType
	} = eventData;
	const position = specialPlacementType || section;
	const specialCategoryLowerCase = specialCategory.toLowerCase();
	const promoLabel = `${specialCtaType && specialCtaType} ${
		specialYear && ` - ${specialYear}`
	}${specialMake && ` ${specialMake}`}${specialModel && ` ${specialModel}`}${
		specialIncentiveType && ` - ${specialIncentiveType}`
	}`;
	const text = element && element.innerText ? element.innerText : specialCta;
	if (
		specialCategoryLowerCase === 'service' ||
		specialCategoryLowerCase === 'parts'
	) {
		return `${position}:${specialHeading}:${text}`;
	}
	return `${position}:${promoLabel}:${text}`;
};

export const getComparePageClickLinkText = ({ eventData }) => {
	const { containedImg, altText, linkText } = eventData;

	if (containedImg) {
		return altText;
	}
	return linkText;
};

// need this for MyCars events because MyCars events don't have vehicle.inventoryStatus
// they have vehicle.status - maybe we can map from that
export const getVehicleInfo = ({
	eventData: { vehicle = {} } = {},
	vehicles = []
}) => {
	const veh = vehicles.find((v) => v.uuid === vehicle?.uuid);
	return {
		condition: veh?.inventoryType,
		make: veh?.make,
		modelName: veh?.model,
		modelYear: veh?.modelYear,
		trimLevel: veh?.trim,
		vin: veh?.vin
	};
};

export const getVideoName = ({ eventData: { videoSource = '' } = {} } = {}) => {
	if (videoSource.includes('carflix')) {
		const { make, model, modelYear } =
			(window?.DDC?.dataLayer?.vehicles &&
				window?.DDC?.dataLayer?.vehicles.length &&
				window?.DDC?.dataLayer?.vehicles[0]) ||
			{};
		if (make && model && modelYear) {
			return `The ${modelYear} ${make} ${model}`;
		}
	}
	return '';
};
